import { useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { AuthContext } from '@/context/auth-context'
import { ContextRail } from '@/matter/components/matter-overview/ContextRail'
import { ContextRailDriveSyncStatus } from '@/matter/components/matter-overview/context-rail-drive-sync-status'
import type { MatterRead } from '@/matter/store/schemas'
import { canEditMatterSettings } from '@/matter/utils/matter-permissions'
import { isSampleMatter } from '@/matter/utils/is-sample-matter'
import { matterPaths } from '@/routing/paths'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { useOpenConnectedAccountsSettings } from '@/settings-modal/hooks/useOpenConnectedAccountsSettings'
import { usePollWhileVisible } from '@/hooks/usePollWhileVisible'
import { pickVisibleAccount } from '@/settings/utils/pick-visible-account'
import { useAppSelector } from '@/store/store-hooks'
import { kProcessingPollIntervalMs } from '@/uppy-file-upload/constants'
import { selectConnectedAccounts } from '@/user_files/external-storage/store/slice'
import { SelectExternalFolderModal } from '@/user_files/external-storage/components/SelectExternalFolderModal'
import { useMatterExternalSyncStatus } from '@/user_files/external-storage/hooks/useMatterExternalSyncStatus'
import { isMatterSyncConnector } from '@/user_files/external-storage/utils/is-matter-sync-connector'
import { useMatterFileCounts } from '@/user_files/folders/hooks/useMatterFileCounts'

type MatterContextRailProps = {
  matter: MatterRead
  onEditMatterDetails: () => void
  onEditJurisdiction: () => void
}

export function MatterContextRail({ matter, onEditMatterDetails, onEditJurisdiction }: MatterContextRailProps) {
  const navigate = useNavigate()
  const openConnectedAccountsSettings = useOpenConnectedAccountsSettings()
  const { trackEvent } = useAnalytics()
  const { user, userAccountData } = useContext(AuthContext)
  const [selectFolderOpen, setSelectFolderOpen] = useState(false)

  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const driveSyncAvailable = driveSyncEnabled && canEditMatterSettings(matter) && !isSampleMatter(matter)

  const { connectedProvider, syncedProvider, activeMapping, hasActiveMatterSync, mapping, connectedAccount, loading, error, reload, reloadMapping } =
    useMatterExternalSyncStatus({ matterId: matter.id, enabled: driveSyncAvailable })

  // useMatterExternalSyncStatus only surfaces healthy accounts. Keep the full account list for
  // pre-connection status and use the mapping's account status for the reconnect state.
  const accounts = useAppSelector(selectConnectedAccounts)
  const visibleAccount = useMemo(() => pickVisibleAccount(accounts), [accounts])

  const { counts, refresh: refreshCounts } = useMatterFileCounts({
    matterId: matter.id,
    syncedFolderId: activeMapping?.user_folder_id ?? null,
    enabled: driveSyncAvailable && !loading && !error,
  })

  // Keep the counts moving while a sync is still landing files.
  const syncLandingFiles = activeMapping != null && (activeMapping.last_synced_at == null || activeMapping.has_pending_files === true)
  usePollWhileVisible(
    () => {
      reloadMapping()
      refreshCounts()
    },
    kProcessingPollIntervalMs,
    driveSyncAvailable && syncLandingFiles
  )

  // Every member sees the disconnected state, but only the connector can reconnect.
  const isSyncConnector = isMatterSyncConnector(activeMapping, user?.uid)

  // While the status is loading or errored the rail has nothing trustworthy to report, so it drops
  // the footer rather than claiming "Connect a file drive" to someone who already has one.
  let driveSyncStatus: ContextRailDriveSyncStatus | null = null
  if (driveSyncAvailable && !loading && !error) {
    if (activeMapping && activeMapping.account_status !== 'connected') {
      driveSyncStatus = ContextRailDriveSyncStatus.Disconnected
    } else if (hasActiveMatterSync) {
      driveSyncStatus = ContextRailDriveSyncStatus.Synced
    } else if (visibleAccount != null && visibleAccount.status !== 'connected') {
      driveSyncStatus = ContextRailDriveSyncStatus.Disconnected
    } else if (visibleAccount == null) {
      driveSyncStatus = ContextRailDriveSyncStatus.NotConnected
    } else {
      driveSyncStatus = ContextRailDriveSyncStatus.NoFolderSynced
    }
  }

  const provider = syncedProvider ?? connectedProvider ?? visibleAccount?.provider ?? null
  const organizationId = getDriveSyncOrganizationId(userAccountData)

  // Pre-connection discovery, so this must not reuse MatterDriveSyncSetupEntryClicked — that
  // event is reserved for post-connection folder-picker entry.
  const navigateToDriveSettings = () => {
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncDiscoveryClicked,
      { matterId: matter.id, organizationId, matterDriveSyncEntrySource: MatterDriveSyncEntrySource.MatterOverview },
      trackEvent
    )
    openConnectedAccountsSettings(SettingsEntrySource.MatterOverviewConnectDrive)
  }

  const handleDriveSyncAction = () => {
    switch (driveSyncStatus) {
      case ContextRailDriveSyncStatus.Synced:
        navigate(matterPaths.settings(matter.id))
        return
      case ContextRailDriveSyncStatus.NoFolderSynced:
        trackMatterDriveSyncEvent(
          AnalyticsEvent.MatterDriveSyncSetupEntryClicked,
          {
            matterId: matter.id,
            organizationId,
            ...(connectedAccount ? { externalStorageProvider: connectedAccount.provider } : {}),
            matterDriveSyncEntrySource: MatterDriveSyncEntrySource.MatterOverview,
          },
          trackEvent
        )
        setSelectFolderOpen(true)
        return
      case ContextRailDriveSyncStatus.Disconnected:
        // Non-connectors go to matter settings, which shows who manages the sync.
        if (activeMapping && !isSyncConnector) {
          navigate(matterPaths.settings(matter.id))
          return
        }
        navigateToDriveSettings()
        return
      case ContextRailDriveSyncStatus.NotConnected:
        navigateToDriveSettings()
        return
      default:
        return
    }
  }

  return (
    <>
      <ContextRail
        matter={matter}
        driveSyncStatus={driveSyncStatus}
        provider={provider}
        syncedFolderPath={activeMapping?.external_folder_path}
        canReconnect={activeMapping == null || isSyncConnector}
        syncedFileCount={counts?.syncedFileCount}
        uploadedFileCount={counts?.uploadedFileCount}
        onEditMatterDetails={onEditMatterDetails}
        onOpenMatterFiles={() => navigate(matterPaths.userFiles.root(matter.id))}
        onEditJurisdiction={onEditJurisdiction}
        onDriveSyncAction={handleDriveSyncAction}
      />
      {driveSyncAvailable && connectedAccount && (
        <SelectExternalFolderModal
          account={connectedAccount}
          currentExternalFolderId={mapping?.external_folder_id}
          matterId={matter.id}
          open={selectFolderOpen}
          onOpenChange={setSelectFolderOpen}
          replaceExistingMapping={mapping != null}
          matterDriveSyncEntrySource={MatterDriveSyncEntrySource.MatterOverview}
          onSuccess={() => reload()}
        />
      )}
    </>
  )
}
